/* eslint-disable @typescript-eslint/no-explicit-any */
import { isEqual } from 'lodash';
import { FC } from 'react';
import { FieldErrors, FieldValues, UseFormClearErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { CreateRequestQuoteModel, CreateRFQDraftModel, CreateRFQModel } from '@steelbuy/data-access';
import {
    MaterialCoatingSelection,
    MaterialCoatingThicknessSelection,
    MaterialFinishSelection,
    MaterialGenericSelection,
    MaterialGradeSelection,
    MaterialSpecificationSelection,
    MaterialSurfaceSelection,
    MaterialToleranceSelection,
    RFQNumberInput,
    RFQThicknessInput,
    RFQWidthInput,
} from '@steelbuy/form';
import {
    AnyMaterialDimensionModel,
    AnyMaterialModel,
    Definition,
    DraftQuoteEntry,
    MaterialProperties,
    MaterialWithDimensions,
    Product,
    ProductType,
    QuoteEntry,
    RFQBuyerDetailsModel,
    RFQDetailsHeadingConfig,
    RFQDraftModel,
    Shape,
    ThicknessOption,
    ThicknessType,
    getCoatingThickness,
    getCoatingThicknessValue,
    getCoatings,
    getCoatingWeights,
    getCompliances,
    getFinishes,
    getGradeFamily,
    getGrades,
    getSpecifications,
    getSurfaceFinishes,
    getSurfaces,
    getSurfaceTreatments,
    isMaterialWithCoating,
    isMaterialWithCoatingThickness,
    isMaterialWithCoatingWeight,
    isMaterialWithCompliance,
    isMaterialWithDefinition,
    isMaterialWithGrade,
    isMaterialWithGradeFamily,
    isMaterialWithPlateType,
    isMaterialWithSpecification,
    isMaterialWithSurface,
    isMaterialWithSurfaceFinish,
    isMaterialWithSurfaceTreatment,
    rfqHeadings,
    createDateFromTimestamp,
    createTimestampFromDate,
    formatInputToNumber,
    formatNumberForInput,
    formatNumberForDimensions,
    isMaterialWithTemper,
    getTempers,
    Quality,
} from '@steelbuy/ts-shared';
import { entries, getThicknessTypes } from '@steelbuy/util';
import { RFQStep } from './CreateRFQ.enums';
import {
    PartialRFQTypeWithDefinition,
    PartialRFQTypeWithPlateType,
    RFQType,
    Step2Materials,
    Step2Type,
    Step3Type,
    isStep1WithDefinition,
    isStep1WithPlateType,
    isStep2,
    isStep3,
    isStep3RequestQuote,
} from './Schema';
import { RoutePath } from '../../router/Routes';

export const getRFQSteps = (isRequestQuote: boolean) => [
    RFQStep.CHOOSE_MATERIALS,
    RFQStep.ADD_ITEMS,
    isRequestQuote ? RFQStep.COMMENTS : RFQStep.SHIPPING_DETAILS,
];

export const isStep1Valid = (formData?: Partial<RFQType>) =>
    isStep1WithDefinition(formData) || isStep1WithPlateType(formData);

export const isStep2Valid = (formData?: Partial<RFQType>) => {
    let materialIndexes: (ProductType | Definition)[] = [];
    if (isStep1WithPlateType(formData)) {
        materialIndexes = formData.plateType;
    } else if (isStep1WithDefinition(formData)) {
        materialIndexes = formData.definition;
    }

    return (
        (formData as Step2Type)?.materials &&
        materialIndexes.length > 0 &&
        materialIndexes.length === Object.values((formData as Step2Type).materials).length &&
        Object.values((formData as Step2Type)?.materials).every((field) => field.length > 0)
    );
};
export const isStep3Valid = (formData?: Partial<RFQType>) => {
    const step3FormData = formData as Step3Type;
    if (step3FormData?.origin?.length > 0 && step3FormData?.delivery && step3FormData?.deadline) {
        return true;
    }
    return false;
};

export const getAddedItemsIndexes = (formData?: RFQType) => {
    if (!formData || !isStep2(formData)) {
        return [];
    }
    return entries((formData as Step2Type)?.materials)
        .filter(([_index, fields]) => fields.length > 0)
        .map(([index, _fields]) => index);
};

export const rfqValidationLookup = {
    [RFQStep.CHOOSE_MATERIALS]: isStep1Valid,
    [RFQStep.ADD_ITEMS]: isStep2Valid,
    [RFQStep.SHIPPING_DETAILS]: isStep3Valid,
    [RFQStep.COMMENTS]: () => true,
};

export type RFQInputs = Record<
    keyof MaterialWithDimensions,
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Component: FC<any>;
        checkFn?: (material: MaterialProperties) => boolean;
        disabledFn?: (material: MaterialProperties) => boolean;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rules?: Record<string, any>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rulesMin?: Record<string, any>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rulesMax?: Record<string, any>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getOptions?: (material: MaterialProperties) => any;
        valueMapper?: (material: MaterialProperties) => string;
        mapInput?: (material: MaterialProperties) => string | number;
        mapStateForInput?: (material: MaterialProperties) => string;
        translationKey?: string;
        endAdornment?: string;
    } & RFQDetailsHeadingConfig
>;

const valueMapperForNumberInput = (field: keyof MaterialWithDimensions) => (value: MaterialWithDimensions) =>
    formatNumberForDimensions(value[field as NonNullable<keyof MaterialWithDimensions>] as number);

export const getThicknessOptions = () => [ThicknessOption.SINGLE, ThicknessOption.RANGE];

type TFn = (key: string, opts?: Record<string, unknown>) => string;

export const validateThicknessMin =
    (t: TFn) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any, formValues: any): string | true => {
        const min = formatInputToNumber(value);
        const max = formatInputToNumber(formValues.thicknessMax);
        if (!Number.isFinite(min) || min <= 0) return t('uiDomain:validation.numberLargerThan', { min: 0 });
        if (Number.isFinite(max) && min >= max) return t('uiDomain:validation.mustBeLessThan');
        return true;
    };

export const validateDimensionMinPositive =
    (t: TFn) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any): string | true => {
        const min = formatInputToNumber(value);
        if (!Number.isFinite(min) || min <= 0) return t('uiDomain:validation.numberLargerThan', { min: 0 });
        return true;
    };

export const validateDimensionMinLessThanMax =
    (t: TFn, key: 'width' | 'thickness') =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any, formValues: any): string | true => {
        const min = formatInputToNumber(value);
        const rawMax = key === 'width' ? formValues.widthMax : formValues.thicknessMax;
        // Only apply cross-field check when max has actually been entered
        if (!rawMax && rawMax !== 0) return true;
        const max = formatInputToNumber(rawMax);
        if (Number.isFinite(min) && min > 0 && Number.isFinite(max) && max > 0 && min >= max)
            return t('uiDomain:validation.mustBeLessThan');
        return true;
    };

export const validateThicknessMax =
    (t: TFn) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any, formValues: any): string | true => {
        const max = formatInputToNumber(value);
        const min = formatInputToNumber(formValues.thicknessMin);
        if (!Number.isFinite(max) || max <= 0) return t('uiDomain:validation.numberLargerThan', { min: 0 });
        if (Number.isFinite(min) && max <= min) return t('uiDomain:validation.mustBeGreaterThan');
        return true;
    };

export const validateDimensionMaxPositive =
    (t: TFn) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any): string | true => {
        const max = formatInputToNumber(value);
        if (!Number.isFinite(max) || max <= 0) return t('uiDomain:validation.numberLargerThan', { min: 0 });
        return true;
    };

export const validateDimensionMaxGreaterThanMin =
    (t: TFn, key: 'width' | 'thickness') =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any, formValues: any): string | true => {
        const max = formatInputToNumber(value);
        const rawMin = key === 'thickness' ? formValues.thicknessMin : formValues.widthMin;
        // Only apply cross-field check when min has actually been entered
        if (!rawMin && rawMin !== 0) return true;
        const min = formatInputToNumber(rawMin);
        if (Number.isFinite(max) && max > 0 && Number.isFinite(min) && min > 0 && max <= min)
            return t('uiDomain:validation.mustBeGreaterThan');
        return true;
    };

export const rfqInputs: RFQInputs = {
    grade: {
        ...rfqHeadings.grade,
        Component: MaterialGradeSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithGrade(material),
        disabledFn: (material: MaterialProperties) => getGrades(material).length === 0,
        getOptions: (material: MaterialProperties) => getGrades(material),
    },
    specification: {
        ...rfqHeadings.specification,
        Component: MaterialSpecificationSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithSpecification(material),
        disabledFn: (material: MaterialProperties) => getSpecifications(material).length === 0,
        getOptions: (material: MaterialProperties) => getSpecifications(material),
    },
    finish: {
        ...rfqHeadings.finish,
        Component: MaterialFinishSelection,
        disabledFn: (material: MaterialProperties) => getFinishes(material).length === 0,
        getOptions: (material: MaterialProperties) => getFinishes(material),
    },
    width: {
        ...rfqHeadings.width,
        Component: RFQWidthInput,
        mapInput: formatInputToNumber,
        valueMapper: (data: MaterialWithDimensions) => {
            if (data.widthMin !== undefined && data.widthMax !== undefined) {
                return `${formatNumberForDimensions(data.widthMin)} - ${formatNumberForDimensions(data.widthMax)}`;
            }
            if (data.widthMin !== undefined || data.widthMax !== undefined) {
                return '';
            }
            return formatNumberForDimensions(data.width as number);
        },
        mapStateForInput: formatNumberForInput,
    },
    thickness: {
        ...rfqHeadings.thickness,
        Component: RFQThicknessInput,
        mapInput: formatInputToNumber,
        valueMapper: (data: MaterialWithDimensions) => {
            if (data.thicknessMin !== undefined && data.thicknessMax !== undefined) {
                return `${formatNumberForDimensions(data.thicknessMin)} - ${formatNumberForDimensions(
                    data.thicknessMax
                )}`;
            }
            if (data.thicknessMin !== undefined || data.thicknessMax !== undefined) {
                return '';
            }
            return formatNumberForDimensions(data.thickness as number);
        },
        mapStateForInput: formatNumberForInput,
    },
    weight: {
        ...rfqHeadings.weight,
        Component: RFQNumberInput,
        mapInput: formatInputToNumber,
        valueMapper: valueMapperForNumberInput('weight'),
        mapStateForInput: formatNumberForInput,
    },
    tolerance: {
        ...rfqHeadings.tolerance,
        Component: MaterialToleranceSelection,
    },
    length: {
        ...rfqHeadings.length,
        Component: RFQNumberInput,
    },
    surface: {
        ...rfqHeadings.surface,
        Component: MaterialSurfaceSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithSurface(material),
        disabledFn: (material: MaterialProperties) => getSurfaces(material).length === 0,
        getOptions: (material: MaterialProperties) => getSurfaces(material),
    },
    coating: {
        ...rfqHeadings.coating,
        Component: MaterialCoatingSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithCoating(material),
        disabledFn: (material: MaterialProperties) => getCoatings(material).length === 0,
        getOptions: (material: MaterialProperties) => getCoatings(material),
    },
    coatingThicknessValue: {
        ...rfqHeadings.coatingThicknessValue,
        Component: MaterialCoatingThicknessSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithCoatingThickness(material),
        disabledFn: (material: MaterialProperties) => getCoatingThickness(material).length === 0,
        getOptions: (material: MaterialProperties) => getCoatingThickness(material),
        valueMapper: getCoatingThicknessValue,
    },
    gradeFamily: {
        ...rfqHeadings.gradeFamily,
        Component: MaterialGenericSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithGradeFamily(material),
        disabledFn: (material: MaterialProperties) => getGradeFamily(material).length === 0,
        getOptions: (material: MaterialProperties) => getGradeFamily(material),
        translationKey: 'material.gradeFamily.value',
    },
    coatingWeight: {
        ...rfqHeadings.coatingWeight,
        Component: MaterialGenericSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithCoatingWeight(material),
        disabledFn: (material: MaterialProperties) => getCoatingWeights(material).length === 0,
        getOptions: (material: MaterialProperties) => getCoatingWeights(material),
        translationKey: 'material.coatingWeight.value',
    },
    surfaceTreatment: {
        ...rfqHeadings.surfaceTreatment,
        Component: MaterialGenericSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithSurfaceTreatment(material),
        disabledFn: (material: MaterialProperties) => getSurfaceTreatments(material).length === 0,
        getOptions: (material: MaterialProperties) => getSurfaceTreatments(material),
        translationKey: 'material.surfaceTreatment.value',
    },
    compliance: {
        ...rfqHeadings.compliance,
        Component: MaterialGenericSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithCompliance(material),
        disabledFn: (material: MaterialProperties) => getCompliances(material).length === 0,
        getOptions: (material: MaterialProperties) => getCompliances(material),
        translationKey: 'material.compliance.value',
    },
    surfaceFinish: {
        ...rfqHeadings.surfaceFinish,
        Component: MaterialGenericSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithSurfaceFinish(material),
        disabledFn: (material: MaterialProperties) => getSurfaceFinishes(material).length === 0,
        getOptions: (material: MaterialProperties) => getSurfaceFinishes(material),
        translationKey: 'material.surfaceFinish.value',
    },
    temper: {
        ...rfqHeadings.temper,
        Component: MaterialGenericSelection,
        checkFn: (material: MaterialProperties) => isMaterialWithTemper(material),
        disabledFn: (material: MaterialProperties) => getTempers(material).length === 0,
        getOptions: (material: MaterialProperties) => getTempers(material),
        translationKey: 'material.temper.value',
    },
    thicknessType: {
        Component: MaterialGenericSelection,
        checkFn: () => true,
        getOptions: (material: MaterialProperties) => {
            if (material.thicknessOption === ThicknessOption.RANGE) return [];
            if (material.product === Product.SHEET) return [ThicknessType.NOM];
            return getThicknessTypes();
        },
        disabledFn: (material: MaterialProperties) =>
            material.product === Product.SHEET || material.thicknessOption === ThicknessOption.RANGE,
        translationKey: 'material.thicknessType.value',
    },
    thicknessOption: {
        Component: MaterialGenericSelection,
        checkFn: () => true,
        getOptions: () => getThicknessOptions(),
        disabledFn: (material: MaterialProperties) =>
            material.product === Product.SHEET && material.thicknessOption === ThicknessOption.SINGLE,
        translationKey: 'material.thicknessOption.value',
    },
    widthOption: {
        Component: MaterialGenericSelection,
        checkFn: () => true,
        getOptions: () => getThicknessOptions(),
        translationKey: 'material.thicknessOption.value',
    },
    quality: {
        Component: MaterialGenericSelection,
        getOptions: () => [Quality.PRIME, Quality.EXCESS, Quality.SECONDARY],
        translationKey: 'material.quality.value',
    },
} as unknown as RFQInputs;

const numberFields = new Set([
    'weight',
    'length',
    'width',
    'thickness',
    'thicknessMin',
    'thicknessMax',
    'widthMin',
    'widthMax',
]);
const IGNORED_UNIQUENESS_FIELDS = new Set(['id', 'weight', 'thicknessOption', 'widthOption']);

export const isUniqueEntry = (
    formData: MaterialWithDimensions & { id?: string },
    fields: MaterialWithDimensions[],
    ignoreIndex?: number
) => {
    let isUnique = true;
    fields.forEach((field, index) => {
        if (isUnique && ignoreIndex !== index) {
            isUnique = !entries(formData).every(([key, value]) => {
                const fieldValue = field[key as NonNullable<keyof MaterialWithDimensions>];
                const isNumberField = numberFields.has(key);
                const formattedValue = isNumberField && value !== undefined ? parseFloat(`${value}`) : value;
                const formattedFieldValue = isNumberField ? parseFloat(`${fieldValue}`) : fieldValue;
                return IGNORED_UNIQUENESS_FIELDS.has(key) || !formattedValue || formattedFieldValue === formattedValue;
            });
        }
    });
    return isUnique;
};

export const updateNonApplicableFields = (
    formHeadings: RFQInputs,
    material: MaterialWithDimensions,
    clearErrors: UseFormClearErrors<FieldValues>,
    watch: UseFormWatch<FieldValues>,
    setValue: UseFormSetValue<FieldValues>,
    errors: FieldErrors<FieldValues>
) => {
    entries(formHeadings).forEach(([key, value]) => {
        const options = value.getOptions?.({ ...material, ...watch() });
        if (options && options.length === 0 && errors[key]) {
            clearErrors(key);
        }
        if (options && watch(key) && (options?.length === 0 || !options?.includes(watch(key)))) {
            setValue(key, undefined);
        }
    });
};

export const removeNullFields = (data: MaterialWithDimensions) =>
    entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            acc[key] = value as any;
        }
        return acc;
    }, {} as MaterialWithDimensions);

const mapFields = (data: MaterialWithDimensions, mapperName: 'mapInput' | 'mapStateForInput' = 'mapInput') =>
    entries(data).reduce(
        (acc, [key, value]) => {
            const mapper = rfqInputs[key]?.[mapperName];
            if (typeof mapper === 'function') {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                acc[key] = mapper(value as MaterialProperties) as any;
            }
            return acc;
        },
        { ...data } as MaterialWithDimensions
    );

export const mapInputs = (data: MaterialWithDimensions) => mapFields(data, 'mapInput');

export const mapStateForInput = (data: MaterialWithDimensions) => mapFields(data, 'mapStateForInput');
export const hasFormData = (formData?: RFQType) => formData && Object.keys(formData).length;

const DIMENSION_FIELDS = ['width', 'length', 'thickness'];
const MATERIAL_OMIT_FIELDS = [
    ...DIMENSION_FIELDS,
    'thicknessMin',
    'thicknessMax',
    'widthMin',
    'widthMax',
    'weight',
    'plateType',
    'definition',
    'thicknessOption',
    'widthOption',
    'deliveryAddress',
    'thicknessType',
    'quality',
];

const getDimensions = (materials: MaterialWithDimensions, product: Product) =>
    DIMENSION_FIELDS.reduce(
        (acc, field: string) => {
            if (materials[field as keyof AnyMaterialDimensionModel] !== undefined) {
                const value = materials[field as keyof MaterialWithDimensions];
                acc[field as keyof AnyMaterialDimensionModel] = Number.isNaN(value) ? value : parseFloat(`${value}`);
            }
            return acc;
        },
        { product } as AnyMaterialDimensionModel
    );

const getMaterials = (materials: MaterialWithDimensions) =>
    entries(materials).reduce((acc, [field, value]) => {
        if (field !== undefined && value !== undefined && !MATERIAL_OMIT_FIELDS.includes(field)) {
            // have to use any as typescript isn't clever enough to realise that the field cannot be one of the MATERIAL_OMIT_FIELDS
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (acc as any)[field] = value;
        }
        return acc as MaterialProperties;
    }, {} as MaterialProperties);

type PlateOrDefinition = 'plateType' | 'definition';

export const getCreateRFQModel = (formData: RFQType): CreateRFQModel | null => {
    if (isStep3(formData)) {
        const monthAndYear = formData.delivery.split('_');
        let materialIndexes: (ProductType | Definition)[] = [];
        let materialIndexer: PlateOrDefinition | undefined;
        if (isStep1WithPlateType(formData)) {
            materialIndexer = 'plateType';
            materialIndexes = formData.plateType;
        } else if (isStep1WithDefinition(formData)) {
            materialIndexer = 'definition';
            materialIndexes = formData.definition;
        }

        const baseMaterial = {
            materialType: formData.materialType,
            shape: formData.shape,
            product: formData.product,
        };

        if (monthAndYear)
            return {
                expiresAt: new Date(parseInt(formData.deadline, 10)).toISOString(),
                origin: formData.origin,
                additionalInformation: formData.comments,
                delivery: { month: parseInt(monthAndYear[0], 10), year: parseInt(monthAndYear[1], 10) },
                quoteEntry: materialIndexes.reduce((acc, index) => {
                    const quoteEntries = formData.materials[index].map((materials) => ({
                        weight: parseFloat(`${materials.weight}`),
                        materialDimensions: getDimensions(materials, formData.product),
                        ...(materials.deliveryAddress ? { deliveryAddress: materials.deliveryAddress } : {}),
                        material: {
                            ...(materialIndexer ? { [materialIndexer]: index } : {}),
                            ...baseMaterial,
                            ...getMaterials(materials),
                        },
                    }));
                    acc.push(...(quoteEntries as QuoteEntry[]));
                    return acc;
                }, [] as QuoteEntry[]),
            };
    }
    return null;
};

export const getCreateRequestQuoteModel = (formData: RFQType): CreateRequestQuoteModel | null => {
    if (isStep3RequestQuote(formData)) {
        let materialIndexes: (ProductType | Definition)[] = [];
        let materialIndexer: PlateOrDefinition | undefined;
        if (isStep1WithDefinition(formData)) {
            materialIndexer = 'definition';
            materialIndexes = formData.definition;
        }

        const baseMaterial = {
            materialType: formData.materialType,
            shape: formData.shape,
            product: formData.product,
        };

        return {
            additionalInformation: formData.comments,
            quoteEntry: materialIndexes.reduce((acc, index) => {
                const quoteEntries = formData.materials[index].map((materials) => ({
                    weight: Number.parseFloat(`${materials.weight}`),
                    materialDimensions: {
                        ...getDimensions(materials, formData.product),
                        ...(materials.thicknessMin && { thicknessMin: materials.thicknessMin }),
                        ...(materials.thicknessMax && { thicknessMax: materials.thicknessMax }),
                        ...(materials.thicknessOption && { thicknessOption: materials.thicknessOption }),
                        ...(materials.thicknessType && { thicknessType: materials.thicknessType }),
                        ...(materials.widthMin && { widthMin: materials.widthMin }),
                        ...(materials.widthMax && { widthMax: materials.widthMax }),
                        ...(materials.widthOption && { widthOption: materials.widthOption }),
                    },
                    ...(materials.deliveryAddress && { deliveryAddressId: materials.deliveryAddress }),
                    material: {
                        ...(materialIndexer ? { [materialIndexer]: index } : {}),
                        ...baseMaterial,
                        ...getMaterials(materials),
                    },
                    ...(materials.quality ? { quality: materials.quality } : {}),
                }));
                acc.push(...(quoteEntries as QuoteEntry[]));
                return acc;
            }, [] as QuoteEntry[]),
        };
    }
    return null;
};

const DRAFT_RFQ_DEFAULTS = {
    origin: [],
    delivery: {},
    quoteEntry: [],
};

export const getCreateRFQDraftModel = (formData: Partial<RFQType>): CreateRFQDraftModel | null => {
    let payload: CreateRFQDraftModel = { ...DRAFT_RFQ_DEFAULTS };
    if (isStep3(formData)) {
        const monthAndYear = formData?.delivery?.split('_');
        payload = {
            ...payload,
            origin: formData?.origin || DRAFT_RFQ_DEFAULTS.origin,
            delivery: monthAndYear
                ? { month: parseInt(monthAndYear[0], 10), year: parseInt(monthAndYear[1], 10) }
                : DRAFT_RFQ_DEFAULTS.delivery,
        };
        if (formData?.deadline) {
            payload.expiresAt = createTimestampFromDate(new Date(parseInt(formData.deadline, 10)));
        }
        if (formData?.comments) {
            payload.additionalInformation = formData.comments;
        }
    }
    let materialIndexes: (ProductType | Definition)[] = [];
    let materialIndexer: 'plateType' | 'definition' | undefined;
    if (isStep1WithPlateType(formData)) {
        materialIndexer = 'plateType';
        materialIndexes = formData.plateType;
    } else if (isStep1WithDefinition(formData)) {
        materialIndexer = 'definition';
        materialIndexes = formData.definition;
    }

    const baseMaterial = {
        materialType: formData.materialType,
        shape: formData.shape,
        product: formData.product,
    } as Partial<AnyMaterialModel>;

    let quoteEntry: DraftQuoteEntry[] = [];
    if (materialIndexes.length) {
        quoteEntry = materialIndexes.reduce((acc, index) => {
            const indexer = { ...(materialIndexer ? { [materialIndexer]: index } : {}) };

            const quoteEntries =
                isStep2(formData) && formData?.materials && formData?.materials[index]?.length > 0
                    ? formData.materials[index].map((materials) => ({
                          weight: parseFloat(`${materials.weight}`),
                          materialDimensions: getDimensions(materials, baseMaterial.product || Product.COIL),
                          ...(materials.deliveryAddress ? { deliveryAddress: materials.deliveryAddress } : {}),
                          material: {
                              ...(materialIndexer ? { [materialIndexer]: index } : {}),
                              ...baseMaterial,
                              ...getMaterials(materials),
                          },
                      }))
                    : [
                          {
                              material: { ...baseMaterial, ...indexer },
                              materialDimensions: {},
                              weight: undefined,
                          },
                      ];
            acc.push(...(quoteEntries as DraftQuoteEntry[]));
            return acc;
        }, [] as DraftQuoteEntry[]);
    } else {
        quoteEntry = [
            {
                material: baseMaterial,
                materialDimensions: {},
            },
        ];
    }

    return {
        ...payload,
        quoteEntry,
    };
};

// filter out empty array, undefined, and empty objects
const filterInvalidKeys = (obj: Partial<RFQType>) =>
    Object.entries(obj)
        .filter(
            ([_, v]) =>
                v !== undefined &&
                (!Array.isArray(v) || v.length > 0) &&
                (!(v instanceof Object) || Object.keys(v).length > 0)
        )
        .reduce((r, [k, v]) => {
            const filteredValue = v instanceof Object ? filterInvalidKeys(v) : v;
            if (!(filteredValue instanceof Object && Object.keys(filteredValue).length === 0)) {
                // eslint-disable-next-line no-param-reassign
                r[k] = filteredValue;
            }
            return r;
        }, {} as Record<string, unknown>);

export const hasDraftChanged = (prevDraft: Partial<RFQType>, currentDraft: Partial<RFQType>) => {
    const previousDraft = filterInvalidKeys(prevDraft);
    const updatedDraft = filterInvalidKeys(currentDraft);
    const hasChanges = !isEqual(previousDraft, updatedDraft);
    return hasChanges;
};

const step1Fields = ['materialType', 'shape', 'product', 'definition', 'plateType'];

const GUARDS = {
    definition: isMaterialWithDefinition,
    plateType: isMaterialWithPlateType,
};

const getIndexerAndMaterials = (draftRFQ: RFQDraftModel, indexer: 'definition' | 'plateType') => {
    const guard = GUARDS[indexer];
    return draftRFQ.quoteEntry.reduce(
        (acc, quoteEntry) => {
            const { material } = quoteEntry;
            if (guard(material) && material[indexer]) {
                const indexerValue = material[indexer] as Definition | ProductType;
                if (!acc.indexer.includes(indexerValue)) {
                    acc.indexer.push(indexerValue);
                }
                const rfqMaterials = Object.keys(quoteEntry.material)
                    .filter((key) => !step1Fields.includes(key))
                    .reduce((mappedObj, key: string) => {
                        // eslint-disable-next-line no-param-reassign
                        (mappedObj as any)[key] = quoteEntry.material[key as keyof MaterialProperties];
                        return mappedObj;
                    }, {} as MaterialProperties);
                const materialsForDefinition: Omit<MaterialWithDimensions, 'plateType' | 'definition'> = {
                    ...rfqMaterials,
                    ...(quoteEntry.materialDimensions || {}),
                    ...(quoteEntry.weight ? { weight: quoteEntry.weight } : {}),
                };
                if (Object.keys(materialsForDefinition).length) {
                    if (!acc.materials[indexerValue]) {
                        acc.materials[indexerValue] = [];
                    }
                    acc.materials[indexerValue].push(materialsForDefinition);
                }
            }
            return acc;
        },
        {
            indexer: [] as any[],
            materials: {} as any,
        }
    );
};

export const convertToRFQType = (draftRFQ: RFQDraftModel): Partial<RFQType> | null => {
    if (!draftRFQ.quoteEntry.length) {
        return null;
    }

    const { materialType, product, shape } = draftRFQ.quoteEntry[0].material;
    const rfqType: Partial<RFQType> = {
        materialType,
        shape,
        product,
        origin: draftRFQ.origin,
    };

    if (draftRFQ.delivery.month && draftRFQ.delivery.year) {
        rfqType.delivery = `${draftRFQ.delivery.month}_${draftRFQ.delivery.year}`;
    }

    if (draftRFQ.expiresAt) {
        rfqType.deadline = `${createDateFromTimestamp(draftRFQ.expiresAt).getTime()}`;
    }

    if (draftRFQ.additionalInformation) {
        rfqType.comments = draftRFQ.additionalInformation;
    }

    if (isMaterialWithDefinition(draftRFQ.quoteEntry[0].material)) {
        const { indexer, materials } = getIndexerAndMaterials(draftRFQ, 'definition');
        (rfqType as PartialRFQTypeWithDefinition).definition = indexer;
        rfqType.materials = materials;
    } else if (isMaterialWithPlateType(draftRFQ.quoteEntry[0].material)) {
        const { indexer, materials } = getIndexerAndMaterials(draftRFQ, 'plateType');
        (rfqType as PartialRFQTypeWithPlateType).plateType = indexer;
        rfqType.materials = materials;
    }
    if (rfqType.materials && Object.keys(rfqType.materials).length === 0) {
        rfqType.materials = undefined;
    }
    return rfqType;
};

const getIndexerAndMaterialsForRFQ = (
    rfqData: RFQBuyerDetailsModel
): {
    materials: Step2Materials;
    definition: Definition[];
    plateType: ProductType[];
} => {
    const result = {
        materials: {} as Step2Materials,
        definition: [] as Definition[],
        plateType: [] as ProductType[],
    };

    const processMaterial = (material: MaterialProperties, key: 'definition' | 'plateType') => {
        const value = material[key];
        if (value) {
            if (key === 'definition') {
                if (!result.definition.includes(value as Definition)) {
                    result.definition.push(value as Definition);
                }
            } else if (!result.plateType.includes(value as ProductType)) {
                result.plateType.push(value as ProductType);
            }

            if (!result.materials[value]) {
                result.materials[value] = [];
            }

            const materialWithoutKey = { ...material } as Omit<MaterialWithDimensions, 'plateType' | 'definition'>;
            result.materials[value].push(materialWithoutKey);
        }
    };

    rfqData.quoteEntry.forEach((quoteEntry) => {
        quoteEntry.materials.forEach((material) => {
            if (isMaterialWithDefinition(material)) {
                processMaterial(material, 'definition');
            }
            if (isMaterialWithPlateType(material)) {
                processMaterial(material, 'plateType');
            }
        });
    });

    return { materials: result.materials, definition: result.definition, plateType: result.plateType };
};

export const getSimilarRFQ = (rfqData: RFQBuyerDetailsModel): RFQType => {
    const { definition, plateType, materials } = getIndexerAndMaterialsForRFQ(rfqData);

    const rfqType: RFQType = {
        origin: rfqData.origin,
        materialType: rfqData.quoteEntry[0].materialType,
        shape: rfqData.quoteEntry[0].materials[0].shape as Shape,
        product: rfqData.quoteEntry[0].product,
        definition,
        plateType,
        delivery: '',
        deadline: '',
        materials,
        comments: rfqData.additionalInformation || '',
    };

    return rfqType;
};

export const REQUEST_QUOTE_DEFINITION_ORDER: Definition[] = [
    Definition.HOT_ROLLED,
    Definition.HOT_ROLLED_PICKLED_OILED,
    Definition.COLD_ROLLED,
    Definition.GALVANIZED,
    Definition.GALVANNEAL,
    Definition.GALVALUME,
    Definition.ALUMINIZED,
];

export const requestQuoteDefinitionSortFn = (a: Definition, b: Definition) => {
    const aOrder = REQUEST_QUOTE_DEFINITION_ORDER.indexOf(a);
    const bOrder = REQUEST_QUOTE_DEFINITION_ORDER.indexOf(b);
    const aIndex = aOrder === -1 ? REQUEST_QUOTE_DEFINITION_ORDER.length : aOrder;
    const bIndex = bOrder === -1 ? REQUEST_QUOTE_DEFINITION_ORDER.length : bOrder;
    return aIndex - bIndex;
};

export const getStepRoute = (step: number, isRequestQuote: boolean, draftId?: string | null) => {
    let stepPath = '';
    if (step > 1) {
        stepPath = `/step${step}`;
    }
    if (draftId) {
        return `${
            isRequestQuote ? RoutePath.CREATE_REQUEST_QUOTE_WIZARD : RoutePath.CREATE_RFQ_WIZARD
        }/${draftId}${stepPath}`;
    }
    return `${isRequestQuote ? RoutePath.CREATE_REQUEST_QUOTE_WIZARD : RoutePath.CREATE_RFQ_WIZARD}${stepPath}`;
};
